import React, { useCallback, useState, useRef } from 'react';
import { SideMenu } from './SideMenu';
import { PageType } from '../App';
// Pages that support swipe navigation, in order
const SWIPEABLE_PAGES: PageType[] = ['home', 'chat', 'activities', 'family'];
interface AppLayoutProps {
  children: React.ReactNode;
  currentPage: PageType;
  onNavigate: (page: PageType) => void;
  onNewConversation: () => void;
  onLogout: () => void;
  isMenuOpen?: boolean;
  onMenuClose?: () => void;
}
export function AppLayout({
  children,
  currentPage,
  onNavigate,
  onNewConversation,
  onLogout,
  isMenuOpen = false,
  onMenuClose
}: AppLayoutProps) {
  const closeMenu = () => onMenuClose?.();
  // Swipe state
  const touchStartRef = useRef<{
    x: number;
    y: number;
    time: number;
  } | null>(null);
  const touchCurrentRef = useRef<number>(0);
  const isHorizontalRef = useRef<boolean | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const currentSwipeIndex = SWIPEABLE_PAGES.indexOf(currentPage);
  const isSwipeablePage = currentSwipeIndex !== -1;
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isSwipeablePage) return;
    const touch = e.touches[0];
    // Only start swipe from edge zones (first/last 40px of screen)
    const isEdge = touch.clientX < 40 || touch.clientX > window.innerWidth - 40;
    if (!isEdge) return;
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    touchCurrentRef.current = touch.clientX;
    isHorizontalRef.current = null;
    setIsSwiping(true);
  }, [isSwipeablePage]);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current || !isSwipeablePage) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    // Determine direction on first significant movement
    if (isHorizontalRef.current === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        isHorizontalRef.current = Math.abs(dx) > Math.abs(dy) * 1.5;
      }
    }
    if (isHorizontalRef.current) {
      touchCurrentRef.current = touch.clientX;
      // Limit offset at boundaries
      const canGoLeft = currentSwipeIndex < SWIPEABLE_PAGES.length - 1;
      const canGoRight = currentSwipeIndex > 0;
      let offset = dx;
      if (!canGoLeft && dx < 0 || !canGoRight && dx > 0) {
        offset = dx * 0.2; // Rubber band effect at edges
      }
      setSwipeOffset(offset);
    }
  }, [isSwipeablePage, currentSwipeIndex]);
  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !isHorizontalRef.current || !isSwipeablePage) {
      setIsSwiping(false);
      setSwipeOffset(0);
      touchStartRef.current = null;
      isHorizontalRef.current = null;
      return;
    }
    const dx = touchCurrentRef.current - touchStartRef.current.x;
    const dt = Date.now() - touchStartRef.current.time;
    const velocity = Math.abs(dx) / dt;
    const threshold = window.innerWidth * 0.25;
    const velocityThreshold = 0.4;
    let newIndex = currentSwipeIndex;
    if (dx < -threshold || dx < -30 && velocity > velocityThreshold) {
      // Swiped left → next page
      newIndex = Math.min(currentSwipeIndex + 1, SWIPEABLE_PAGES.length - 1);
    } else if (dx > threshold || dx > 30 && velocity > velocityThreshold) {
      // Swiped right → previous page
      newIndex = Math.max(currentSwipeIndex - 1, 0);
    }
    setSwipeOffset(0);
    setIsSwiping(false);
    touchStartRef.current = null;
    isHorizontalRef.current = null;
    if (newIndex !== currentSwipeIndex) {
      onNavigate(SWIPEABLE_PAGES[newIndex]);
    }
  }, [currentSwipeIndex, isSwipeablePage, onNavigate]);
  return <div className="h-screen bg-yeppy-bg relative overflow-hidden flex flex-col" data-id="element-77">
      <SideMenu isOpen={isMenuOpen} onClose={closeMenu} currentPage={currentPage} onNavigate={onNavigate} onNewConversation={onNewConversation} onLogout={onLogout} data-id="element-78" />

      {/* Main Content Area - with swipe support */}
      <main className="flex-1 flex flex-col overflow-hidden" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} style={{
      transform: isSwiping && swipeOffset !== 0 ? `translateX(${swipeOffset * 0.3}px)` : undefined,
      transition: isSwiping ? 'none' : 'transform 0.3s ease-out'
    }} data-id="element-79">
        {children}
      </main>
    </div>;
}